import { Box, Tooltip } from '@color/continuum';
import { useState, type MouseEvent } from 'react';

const COPIED_FEEDBACK_MS = 800;

export interface CopyableTextProps {
  text: string;
}

// Click-to-copy text used across the variant confirmation list and detail
// views: DataGrid cells, the detail page header (inside a `Title`), `Text`
// elements, and `InfoTable` rows.
//
// Why inline-block instead of block:
//   The detail view places this inside inline flows (`<Title>Confirmation
//   <CopyableText/></Title>`, `<Text><CopyableText/></Text>`) and inside
//   `InfoTable` cells that use `whiteSpace: nowrap` and size to content.
//   Inline-block keeps the clickable/hover area exactly the width of the
//   text in those contexts, while `max-width: 100%` + `overflow: hidden`
//   + `text-overflow: ellipsis` still engages truncation when a parent
//   constrains width (e.g. a `DataGrid` column).
//
// Why `event.stopPropagation()`:
//   The list view's row click opens the detail page in a new tab. Without
//   stopping propagation here, copying a cell would also navigate.
//
// Why empty text renders `null`:
//   Lets empty cells keep delegating their click to the surrounding row
//   (so empty rows in the list still navigate on click) and keeps empty
//   `InfoTable` cells visually empty rather than carrying a tooltip
//   trigger over nothing.
//
// Why the tooltip is controlled by `open` with all openers disabled:
//   We only want the "Copied ✔︎" confirmation to flash after a successful
//   copy; idle hover should not produce a tooltip. The clickable
//   affordance is carried by `cursor: pointer` and the `info.hover`
//   background instead.
export function CopyableText(props: CopyableTextProps) {
  const { text } = props;
  const [copied, setCopied] = useState(false);

  if (!text) return null;

  const handleClick = async (event: MouseEvent) => {
    event.stopPropagation();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <Tooltip
      title="Copied ✔︎"
      open={copied}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      slotProps={{ popper: { disablePortal: false } }}
    >
      <Box
        component="span"
        onClick={handleClick}
        sx={{
          display: 'inline-block',
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'info.hover',
          },
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}
